/**
 * Centralized Error Handler
 * 
 * Provides consistent error handling across the application.
 * Integrates with logging service and Sentry for error tracking.
 */

import * as Sentry from '@sentry/react';
import { AppError } from './AppError';
import { logger } from '../logging';

/**
 * Check if Sentry is initialized by checking for DSN in environment
 */
function isSentryInitialized(): boolean {
  // Check Vite environment variables (client-side)
  try {
    if (typeof import.meta !== 'undefined' && import.meta?.env) {
    return !!(import.meta.env.VITE_SENTRY_DSN || import.meta.env.SENTRY_DSN);
    }
  } catch { void 0 }
  // Fallback to process.env (server-side or build time)
  if (typeof process !== 'undefined' && process.env) {
    return !!(process.env.VITE_SENTRY_DSN || process.env.SENTRY_DSN);
  }
  return false;
}

/**
 * Error handler result containing user-friendly message and technical details
 */
export interface ErrorHandlerResult {
  userMessage: string;
  technicalMessage: string;
  code: string;
  shouldRetry: boolean;
}

/**
 * Determines if an error should be retried based on status code
 * Only transient failures (timeouts, rate limits, server errors) should be retried
 */
function isRetryableError(error: AppError): boolean {
  // Only retry on transient failures
  const retryableStatusCodes = [408, 429, 500, 502, 503, 504];
  return retryableStatusCodes.includes(error.statusCode);
}

/**
 * Handles errors consistently across the application
 * Logs technical details and returns user-friendly messages
 */
export function handleError(error: unknown, context?: string): ErrorHandlerResult {
  const contextPrefix = context ? `[${context}]` : '';
  
  if (error instanceof AppError) {
    // Send to Sentry with context (only if Sentry is initialized)
    if (isSentryInitialized()) {
      Sentry.captureException(error, {
        tags: {
          errorType: 'AppError',
          errorCode: error.code,
          context: context || 'unknown',
        },
        level: error.statusCode >= 500 ? 'error' : 'warning',
        extra: {
          statusCode: error.statusCode,
          isOperational: error.isOperational,
        },
      });
    }
    
    logger.error(`${contextPrefix} ${error.name}: ${error.message}`, {
      code: error.code,
      statusCode: error.statusCode,
      isOperational: error.isOperational,
      stack: error.stack,
    });

    return {
      userMessage: error.message,
      technicalMessage: error.message,
      code: error.code,
      shouldRetry: isRetryableError(error),
    };
  }

  if (error instanceof Error) {
    // Send to Sentry with context (only if Sentry is initialized)
    if (isSentryInitialized()) {
      Sentry.captureException(error, {
        tags: {
          errorType: 'Error',
          context: context || 'unknown',
        },
        level: 'error',
      });
    }
    
    logger.error(`${contextPrefix} Unexpected Error: ${error.message}`, {
      name: error.name,
      stack: error.stack,
    });

    return {
      userMessage: 'An unexpected error occurred. Please try again.',
      technicalMessage: error.message,
      code: 'UNEXPECTED_ERROR',
      shouldRetry: false,
    };
  }

  const stringError = String(error);
  
  // Send unknown errors to Sentry as well (only if Sentry is initialized)
  if (isSentryInitialized()) {
    Sentry.captureMessage(`Unknown Error: ${stringError}`, {
      tags: {
        errorType: 'Unknown',
        context: context || 'unknown',
      },
      level: 'error',
    });
  }
  
  logger.error(`${contextPrefix} Unknown Error: ${stringError}`);

  return {
    userMessage: 'An unknown error occurred. Please try again.',
    technicalMessage: stringError,
    code: 'UNKNOWN_ERROR',
    shouldRetry: false,
  };
}

/**
 * Wraps an async function with error handling
 * Useful for wrapping API calls or other operations
 */
export function withErrorHandling<T>(
  fn: () => Promise<T>,
  context: string
): Promise<T> {
  return fn().catch((error) => {
    const result = handleError(error, context);
    
    // Preserve original AppError if possible
    if (error instanceof AppError) {
      throw error;
    }
    
    // For other errors, create AppError with proper defaults
    const appError = new AppError(
      result.userMessage,
      result.code,
      500 // Default status code for unexpected errors
    );
    throw appError;
  });
}
